/** ONE editor row, for ANY slot.
 *
 *  It is generated from the slot declaration the server sends, so a slot added
 *  in services/loyalty/design.py (a later phase, a later industry) appears here
 *  with no new code. An empty text box means "use the default", and the default
 *  is the profile's own wording IN THAT LANGUAGE — the French box shows the
 *  French default, never the English one.
 *
 *  THE CLINIC TYPES ONE LANGUAGE. A moment after typing stops the other two are
 *  filled by the platform's own translation service (the one the surveys and
 *  campaigns use). A box the clinic typed itself is marked "edited by you" and
 *  is NEVER overwritten; "Translate again" hands it back to the machine.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2 } from 'lucide-react';
import api from '../../lib/api';
import { poorContrast } from './contrast';

const LANGS = [['en', 'EN'], ['fr', 'FR'], ['es', 'ES']];
const field = "w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900";
const WAIT = 900;

export const SlotEditor = ({ slot, value = {}, theme = {}, narrow = false, onChange }) => {
    const { t } = useTranslation();
    const [busyLang, setBusyLang] = useState('');
    const [failed, setFailed] = useState('');
    const timer = useRef(null);
    const latest = useRef(value);
    latest.current = value;

    const set = (key) => (v) => onChange({ ...latest.current, [key]: v });
    const bg = value.bg || theme.button || '';
    const fg = value.fg || theme.button_text || '';
    const warn = slot.kind === 'button' && poorContrast(bg, fg);
    const key = slot.profile_key || slot.default_key;
    const auto = value.auto || [];

    /** The default the phone would show IN THAT LANGUAGE. */
    const defaultFor = (code) => t(key, { lng: code, defaultValue: '' });

    const translate = useCallback(async (source) => {
        const text = String(latest.current[`label_${source}`] || '').trim();
        if (!text) return;
        const targets = LANGS.map(([c]) => c).filter((c) => c !== source
            // never touch a box the clinic typed itself
            && (!latest.current[`label_${c}`] || (latest.current.auto || []).includes(c)));
        if (targets.length === 0) return;
        setBusyLang(source); setFailed('');
        try {
            const r = await api.post('/loyalty/design/translate',
                { text, source_lang: source, targets });
            const got = r.data?.translations || {};
            const next = { ...latest.current };
            const marks = new Set(latest.current.auto || []);
            targets.forEach((c) => {
                const line = String(got[c] || '').trim();
                if (!line) return;             // never save an empty box
                next[`label_${c}`] = line.slice(0, slot.max_len || 200);
                marks.add(c);
            });
            marks.delete(source);
            next.auto = [...marks];
            onChange(next);
        } catch {
            // keep whatever was there before
            setFailed(t('loyalty.design.translateFailed',
                'The translation did not come back — your wording is unchanged'));
        } finally {
            setBusyLang('');
        }
    }, [onChange, slot.max_len, t]);

    useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);

    const typed = (code) => (e) => {
        const next = { ...latest.current, [`label_${code}`]: e.target.value };
        // What she types is hers: it leaves the "automatic" list.
        next.auto = (latest.current.auto || []).filter((c) => c !== code);
        onChange(next);
        if (timer.current) window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => translate(code), WAIT);
    };

    const again = () => {
        const source = LANGS.map(([c]) => c).find(
            (c) => value[`label_${c}`] && !auto.includes(c)) || 'en';
        const others = LANGS.map(([c]) => c).filter((c) => c !== source);
        onChange({ ...latest.current, auto: others });
        window.setTimeout(() => translate(source), 50);
    };

    const anyTyped = LANGS.some(([c]) => value[`label_${c}`]);

    return (
        <div className="rounded-xl border border-slate-200 p-3 dark:border-slate-700/60"
             data-testid={`cc-slot-${slot.id}`}>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                <p className="min-w-0 break-words text-xs font-semibold text-slate-600 dark:text-slate-300">
                    {t(`loyalty.slot.${slot.id}`, slot.id)}
                </p>
                <div className="flex shrink-0 items-center gap-3">
                    {busyLang && (
                        <span className="flex items-center gap-1 text-[11px] text-slate-500"
                              data-testid={`cc-slot-${slot.id}-translating`}>
                            <Loader2 className="h-3 w-3 animate-spin" />
                            {t('loyalty.design.translating', 'translating…')}
                        </span>
                    )}
                    {anyTyped && !busyLang && (
                        <button type="button" className="text-[11px] text-slate-500 underline"
                                data-testid={`cc-slot-${slot.id}-translate-again`}
                                onClick={again}>
                            {t('loyalty.design.translateAgain', 'Translate again')}
                        </button>
                    )}
                    <label className="flex items-center gap-1 text-[11px] text-slate-500">
                        <input type="checkbox" checked={value.visible !== false}
                               data-testid={`cc-slot-${slot.id}-visible`}
                               onChange={(e) => set('visible')(e.target.checked ? true : false)} />
                        {t('loyalty.design.show', 'Show')}
                    </label>
                </div>
            </div>

            <div className={`grid min-w-0 gap-2 ${narrow ? 'grid-cols-1' : 'grid-cols-3'}`}>
                {LANGS.map(([code, label]) => {
                    const own = !!value[`label_${code}`] && !auto.includes(code);
                    return (
                        <label key={code} className="block min-w-0">
                            <span className="mb-1 flex items-center justify-between gap-1 text-[11px] text-slate-500">
                                <span>{label}</span>
                                <span data-testid={`cc-slot-${slot.id}-${code}-origin`}
                                      className={own ? 'text-emerald-600 dark:text-emerald-400' : ''}>
                                    {own ? t('loyalty.design.editedByYou', 'edited by you')
                                        : t('loyalty.design.automatic', 'automatic')}
                                </span>
                            </span>
                            <input className={field} maxLength={slot.max_len}
                                   value={value[`label_${code}`] || ''}
                                   placeholder={defaultFor(code)}
                                   data-testid={`cc-slot-${slot.id}-${code}`}
                                   onChange={typed(code)} />
                        </label>
                    );
                })}
            </div>

            {failed && (
                <p className="mt-2 text-[11px] text-rose-500" data-testid={`cc-slot-${slot.id}-translate-failed`}>
                    {failed}
                </p>
            )}

            {slot.kind === 'button' && (
                <div className="mt-2 flex flex-wrap items-center gap-4">
                    <label className="flex items-center gap-2 text-[11px] text-slate-500">
                        {t('loyalty.design.buttonColour', 'Button')}
                        <input type="color" value={bg || '#f5a623'} data-testid={`cc-slot-${slot.id}-bg`}
                               onChange={(e) => set('bg')(e.target.value)}
                               className="h-8 w-12 rounded border border-slate-200 bg-transparent dark:border-slate-700" />
                    </label>
                    <label className="flex items-center gap-2 text-[11px] text-slate-500">
                        {t('loyalty.design.textColour', 'Text')}
                        <input type="color" value={fg || '#111827'} data-testid={`cc-slot-${slot.id}-fg`}
                               onChange={(e) => set('fg')(e.target.value)}
                               className="h-8 w-12 rounded border border-slate-200 bg-transparent dark:border-slate-700" />
                    </label>
                    {(value.bg || value.fg) && (
                        <button type="button" className="text-[11px] text-slate-500 underline"
                                data-testid={`cc-slot-${slot.id}-colour-reset`}
                                onClick={() => onChange({ ...value, bg: '', fg: '' })}>
                            {t('loyalty.design.useTheme', 'Use the program colours')}
                        </button>
                    )}
                    {warn && (
                        <span className="flex items-center gap-1 text-[11px] text-amber-600 dark:text-amber-400"
                              data-testid={`cc-slot-${slot.id}-contrast`}>
                            <AlertTriangle className="h-3 w-3" />
                            {t('loyalty.design.contrastWarning', 'Hard to read on that colour')}
                        </span>
                    )}
                </div>
            )}

            {slot.switch && (
                <p className="mt-2 text-[11px] text-slate-400">
                    {t('loyalty.design.switchNote', 'Also needs its switch to be on.')}
                </p>
            )}
        </div>
    );
};
